import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSEO from '@/hooks/useSEO';

const cities = [
  {
    name: 'Las Vegas',
    state: 'NV',
    desc: 'The heart of the valley. We serve all Las Vegas neighborhoods including Downtown, Summerlin, and the Strip corridor with premium glass installations.',
    population: '650,000+',
    services: ['Frameless Shower Doors', 'Glass Railings', 'Custom Mirrors', 'Tub Enclosures', 'Glass Partitions'],
    img: 'https://public.readdy.ai/ai/img_res/482f909ffac7c94163657e53329677c2.jpg',
    highlight: 'Most Popular Area',
  },
  {
    name: 'Henderson',
    state: 'NV',
    desc: 'Henderson\'s fastest-growing luxury communities trust Quinolux for custom glass work. From Green Valley to MacDonald Ranch, we cover it all.',
    population: '320,000+',
    services: ['Frameless Shower Doors', 'Glass Railings', 'Custom Mirrors', 'Sliding Doors'],
    img: 'https://readdy.ai/api/search-image?query=Henderson%20Nevada%20suburban%20luxury%20homes%20with%20desert%20mountains%20in%20background%2C%20modern%20residential%20neighborhood%2C%20professional%20real%20estate%20photography&width=800&height=500&seq=city002&orientation=landscape',
    highlight: 'Top Rated',
  },
  {
    name: 'North Las Vegas',
    state: 'NV',
    desc: 'Serving North Las Vegas homeowners and businesses with the same premium glass solutions available across the valley.',
    population: '270,000+',
    services: ['Frameless Shower Doors', 'Tub Enclosures', 'Custom Mirrors', 'Window Glass'],
    img: 'https://readdy.ai/api/search-image?query=North%20Las%20Vegas%20Nevada%20residential%20neighborhood%20with%20modern%20homes%2C%20desert%20landscape%2C%20clear%20blue%20sky%2C%20professional%20photography&width=800&height=500&seq=city003&orientation=landscape',
    highlight: null,
  },
  {
    name: 'Summerlin',
    state: 'NV',
    desc: 'Summerlin\'s luxury master-planned communities deserve the finest glass work. We specialize in high-end residential installations for Summerlin\'s discerning homeowners.',
    population: '100,000+',
    services: ['Frameless Shower Doors', 'Glass Railings', 'Custom Mirrors', 'Stair Glass'],
    img: 'https://readdy.ai/api/search-image?query=Summerlin%20Las%20Vegas%20luxury%20residential%20community%20with%20Red%20Rock%20Canyon%20in%20background%2C%20upscale%20homes%2C%20desert%20landscape%2C%20professional%20photography&width=800&height=500&seq=city004&orientation=landscape',
    highlight: 'Luxury Specialist',
  },
  {
    name: 'Spring Valley',
    state: 'NV',
    desc: 'Spring Valley is one of the most densely populated communities in Nevada. Our team provides fast, reliable glass services throughout the area.',
    population: '180,000+',
    services: ['Frameless Shower Doors', 'Sliding Doors', 'Custom Mirrors', 'Glass Partitions'],
    img: 'https://readdy.ai/api/search-image?query=Spring%20Valley%20Las%20Vegas%20Nevada%20suburban%20neighborhood%20with%20modern%20homes%2C%20palm%20trees%2C%20clear%20sky%2C%20professional%20real%20estate%20photography&width=800&height=500&seq=city005&orientation=landscape',
    highlight: null,
  },
  {
    name: 'Paradise',
    state: 'NV',
    desc: 'Paradise is home to the Las Vegas Strip, major hotels, and thousands of residences. We handle both residential and commercial glass projects in Paradise.',
    population: '220,000+',
    services: ['Glass Partitions', 'Custom Mirrors', 'Frameless Shower Doors', 'Commercial Glass'],
    img: 'https://readdy.ai/api/search-image?query=Paradise%20Nevada%20Las%20Vegas%20Strip%20area%20with%20luxury%20hotels%20and%20modern%20buildings%2C%20vibrant%20cityscape%2C%20professional%20photography&width=800&height=500&seq=city006&orientation=landscape',
    highlight: 'Commercial Specialist',
  },
];

const ServiceAreasPage = () => {
  useSEO({
    title: 'Glass Company Las Vegas Valley | Henderson, Summerlin, North Las Vegas | Quinolux',
    description: 'Quinolux Glass & Mirrors serves all Las Vegas Valley cities — Las Vegas, Henderson, Summerlin, North Las Vegas, Spring Valley, Paradise & Boulder City. Shower doors, glass railings, custom mirrors. Call (702) 604-2093.',
    keywords: 'glass company Las Vegas, shower doors Henderson NV, glass railings Summerlin, glass installation North Las Vegas, custom glass Spring Valley, mirror installation Paradise NV, glass services Las Vegas Valley',
    canonical: 'https://quinoluxglass.com/service-areas',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Glass Company Las Vegas Valley | Service Areas | Quinolux',
        description: 'Quinolux Glass & Mirrors serves all Las Vegas Valley cities — Las Vegas, Henderson, Summerlin, North Las Vegas, Spring Valley, Paradise & Boulder City.',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/service-areas`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/` },
            { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/service-areas` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Custom Glass Installation — Las Vegas Valley',
        provider: {
          '@type': 'LocalBusiness',
          name: 'Quinolux Glass & Mirrors',
          telephone: '+17026042093',
        },
        areaServed: [
          { '@type': 'City', name: 'Las Vegas', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Henderson', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Summerlin', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'North Las Vegas', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Spring Valley', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Paradise', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Boulder City', containedInPlace: { '@type': 'State', name: 'Nevada' } },
        ],
        serviceType: 'Glass Installation',
        description: 'Custom glass installation services including frameless shower doors, glass railings, custom mirrors, and tub enclosures throughout the Las Vegas Valley.',
      },
    ],
  });

  const [activeCity, setActiveCity] = useState(cities[0]);

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0A0A0A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=Las%20Vegas%20Nevada%20aerial%20view%20of%20the%20valley%20with%20mountains%20and%20city%20lights%2C%20professional%20aerial%20photography%2C%20wide%20angle&width=1920&height=600&seq=areahero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">Where We Serve</span>
          <h1 className="text-5xl md:text-6xl font-light text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Service Areas
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Quinolux Glass &amp; Mirrors proudly serves the entire Las Vegas Valley — from Henderson to Summerlin, North Las Vegas to Paradise.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <a
              href="tel:+17026042093"
              className="inline-flex items-center gap-3 bg-white text-black text-sm font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              Call (702) 604-2093
            </a>
            <Link
              to="/services"
              className="inline-flex items-center gap-3 border border-gray-600 text-white text-sm font-medium px-8 py-4 rounded-full hover:border-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* City Selector + Detail */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* City List */}
            <div className="lg:col-span-1">
              <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-6">Select a City</h2>
              <div className="flex flex-col gap-2">
                {cities.map((city) => (
                  <button
                    key={city.name}
                    onClick={() => setActiveCity(city)}
                    className={`flex items-center justify-between px-5 py-4 rounded-xl text-left transition-all duration-300 cursor-pointer ${
                      activeCity.name === city.name
                        ? 'bg-black text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div>
                      <p className="font-semibold text-sm">{city.name}</p>
                      <p className={`text-xs mt-0.5 ${activeCity.name === city.name ? 'text-gray-300' : 'text-gray-400'}`}>
                        {city.population} residents
                      </p>
                    </div>
                    <i className={`ri-arrow-right-line transition-transform duration-300 ${activeCity.name === city.name ? 'translate-x-1' : ''}`}></i>
                  </button>
                ))}
              </div>
            </div>

            {/* City Detail */}
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden border border-gray-100">
                <div className="relative h-64">
                  <img
                    src={activeCity.img}
                    alt={`Glass services in ${activeCity.name}, NV`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-white text-3xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {activeCity.name}, {activeCity.state}
                      </h2>
                      {activeCity.highlight && (
                        <span className="bg-white text-black text-xs font-semibold px-3 py-1 rounded-full">
                          {activeCity.highlight}
                        </span>
                      )}
                    </div>
                    <p className="text-white/70 text-sm">{activeCity.population} residents served</p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">{activeCity.desc}</p>
                  <h3 className="text-black font-semibold text-sm mb-3">Services Available in {activeCity.name}:</h3>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {activeCity.services.map((s) => (
                      <span key={s} className="bg-gray-50 text-gray-700 text-xs px-3 py-1.5 rounded-full border border-gray-100">
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="tel:+17026042093"
                      className="flex-1 flex items-center justify-center gap-2 bg-black text-white text-sm font-medium py-3 rounded-xl hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line"></i>
                      Call for {activeCity.name} Service
                    </a>
                    <Link
                      to="/services"
                      className="flex-1 flex items-center justify-center gap-2 border border-gray-200 text-black text-sm font-medium py-3 rounded-xl hover:border-black transition-colors duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Get Free Quote
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-[#F8F8F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-light text-black mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Find Us on the Map
            </h2>
            <p className="text-gray-500 text-sm">Serving all of Clark County, Nevada</p>
          </div>
          <div className="rounded-2xl overflow-hidden h-96 border border-gray-100">
            <iframe
              title="Quinolux Glass Las Vegas Service Area"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d209550.5!2d-115.2!3d36.17!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80beb782a4f57dd1%3A0x3acf5b61c4d8a6e0!2sLas%20Vegas%2C%20NV!5e0!3m2!1sen!2sus!4v1700000000000"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Ready to Get Started?
          </h2>
          <p className="text-gray-400 text-base mb-10 max-w-xl mx-auto">
            Call us today or request a free quote online. We serve all Las Vegas Valley cities with fast turnaround and premium results.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+17026042093"
              className="inline-flex items-center gap-3 bg-white text-black text-sm font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              (702) 555-0199
            </a>
            <Link
              to="/services"
              className="inline-flex items-center gap-3 border border-gray-600 text-white text-sm font-medium px-8 py-4 rounded-full hover:border-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ServiceAreasPage;
